import { llmRateLimiter } from '../utils/throttle';

/**
 * LLM service - proxies all calls to Netlify serverless functions
 * Uses background functions with job polling for long-running operations
 */

const POLL_INTERVAL_MS = 2000; // 2 seconds between polls
const MAX_POLL_ATTEMPTS = 180; // 6 minutes max wait (180 * 2s)

/**
 * Call LLM proxy and poll for results
 */
async function callLLMProxy(functionName, args) {
  return llmRateLimiter.execute(async () => {
    // 1. Start the job (creates job record in Supabase)
    const startRes = await fetch('/.netlify/functions/llm-proxy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ functionName, args })
    });

    if (!startRes.ok) {
      const error = await startRes.json().catch(() => ({ error: startRes.statusText }));
      throw new Error(error.error || `Failed to start LLM job: ${startRes.status}`);
    }

    const { jobId } = await startRes.json();

    // 2. Invoke background function (returns 202 immediately, processes in background)
    const bgRes = await fetch('/.netlify/functions/llm-proxy-background', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobId })
    });

    if (!bgRes.ok && bgRes.status !== 202) {
      console.error('Background function returned:', bgRes.status);
    }

    // 3. Poll for result
    for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));

      const pollRes = await fetch(`/.netlify/functions/llm-job?id=${jobId}`);

      if (!pollRes.ok) {
        // Job might not be ready yet, continue polling
        if (pollRes.status === 404) continue;
        throw new Error(`Failed to poll job: ${pollRes.status}`);
      }

      const job = await pollRes.json();

      if (job.status === 'completed') {
        return job.result;
      }

      if (job.status === 'failed') {
        throw new Error(job.error || 'LLM job failed');
      }

      // Status is 'pending' or 'in_progress', continue polling
    }

    throw new Error('LLM job timed out after 6 minutes');
  });
}

/**
 * Generate a broad market definition from user prompt
 */
export async function generateMarketDefinition(userPrompt) {
  return callLLMProxy('generateMarketDefinition', { userPrompt });
}

/**
 * Generate alternative search terms for a market
 */
export async function generateSearchTerms(marketDefinition) {
  return callLLMProxy('generateSearchTerms', { marketDefinition });
}

/**
 * Find companies matching a search term in a market
 * @param {string} searchTerm - The search term
 * @param {string} marketDefinition - The market definition
 * @param {function} onCompanyFound - Optional callback called with company name when found
 */
export async function findCompanies(searchTerm, marketDefinition, onCompanyFound) {
  const result = await callLLMProxy('findCompanies', { searchTerm, marketDefinition });

  // Handle callback locally after receiving response
  if (onCompanyFound && result.companies) {
    for (const company of result.companies) {
      onCompanyFound(company.name);
    }
  }

  return result;
}

/**
 * Find products for a company within a market
 */
export async function findProducts(company, marketDefinition) {
  return callLLMProxy('findProducts', { company, marketDefinition });
}

/**
 * Find capabilities and features for products
 */
export async function findCapabilitiesAndFeatures(products, marketDefinition) {
  return callLLMProxy('findCapabilitiesAndFeatures', { products, marketDefinition });
}

/**
 * Analyze which capabilities and features a specific product supports
 */
export async function analyzeProductCapabilities(company, product, capabilities, features) {
  return callLLMProxy('analyzeProductCapabilities', { company, product, capabilities, features });
}
